<script>
  import formatTextUpperCasefirst from "@utils/format-text-upper-casefirst";

  export default {
    props: {
      type: {
        type: String,
        default: 'button',
        validator(value) {
          return [
            'button',
            'submit',
          ].includes(value)
        },
      },
      to: {
        type: [Object,
          String],
        default: ''
      },
      href: {
        type: String,
        default: ''
      },
      target: {
        type: String,
        default: '_blank'
      },
      loading: {
        type: Boolean,
        default: false
      },
      replace: {
        type: Boolean,
        default: false
      },
      disabled: {
        type: Boolean,
        default: false
      }
    },
    methods: {
      formatTextUpperCasefirst,
    },
  };
</script>

<template>
  <a
    v-if="!!href"
    :href="href"
    :target="target"
    :disabled="disabled"
    class="baseButton"
    v-on="$listeners"
  >
    <span class="baseButton__content"><slot/></span>
  </a>
  <router-link
    v-else-if="to"
    :to="to"
    :disabled="disabled"
    class="baseButton"
    active-class="isActive"
    :replace="replace"
    v-on="$listeners"
  >
    <span class="baseButton__content"><slot/></span>
  </router-link>
  <button
    v-else
    :type="type"
    :disabled="disabled"
    class="baseButton"
    :class="{ isLoading: loading }"
    v-on="$listeners"
  >
    <span class="baseButton__content"><slot/></span>
  </button>
</template>
<style lang="scss">
  // stylelint-disable selector-class-pattern

  $colors: (
    "primary": (
      "color": $color-primary,
      "color-hover": $color-primary-hover,
      "text": #FFF,
    ),
    "white": (
      "color": #fff,
      "color-hover": darken(#fff, 5%),
      "text": $color-primary,
    ),
    "secondary": (
      "color": $color-secondary,
      "color-hover": darken($color-secondary, 5%),
      "text": #FFF,
    ),
    "dark": (
      "color": #4D5561,
      "color-hover": darken(#4D5561, 5%),
      "text": #FFF,
    ),
  );

  .baseButton {
    @include link-unstyled;

    display: inline-block;
    width: auto;
    max-width: 100%;
    padding: 1.63rem 1.125rem;
    font-weight: 400;
    color: #FFF;
    text-align: center;
    letter-spacing: 0.15em;
    white-space: nowrap;
    cursor: pointer;
    user-select: none;
    background: grey;
    border: 1px solid transparent;
    opacity: 1;
    transition: background 0.3s, border 0.3s, color 0.3s, opacity 0.3s, backdrop-filter 0.3s;
    -webkit-appearance: none;
    appearance: none;

    @include max-lg {
      padding: 0.8rem 2rem;
      font-size: 0.875rem;
    }

    &:focus {
      outline: none;
    }

    &:disabled, &.isLoading, &.isDisabled {
      opacity: 0.85;
    }

    &:disabled, &.isDisabled {
      color: $color-gray-2 !important;
      cursor: not-allowed;
      background: rgba($color-gray-4, 50%) !important;
    }

    @each $color, $value in $colors {
      &.isStyle#{capitalize($color)} {
        color: map-get($value, 'text');
        background: map-get($value, 'color');

        &:hover:not(:disabled) {
          background: map-get($value, 'color-hover');
        }

        &:active:not(:disabled) {
          background: darken(map-get($value, 'color-hover'), 10%);
        }

        &.isOutline {
          color: map-get($value, 'color');
          border: 1px solid map-get($value, 'color');

          &:hover:not(:disabled) {
            background: map-get($value, 'color-hover');
            border: 1px solid map-get($value, 'color-hover');
          }

          &:active:not(:disabled) {
            background: darken(map-get($value, 'color-hover'), 15%);
          }

          &.isStyleWhite {
            &:hover:not(:disabled) {
              color: #000;
            }
          }
        }

        &.isOutline2 {
          &:hover:not(:disabled) {
            background: map-get($value, 'color');
            border: 1px solid map-get($value, 'color');
          }

          &:active:not(:disabled) {
            background: darken(map-get($value, 'color'), 15%);
          }
        }

        &.isOutline3 {
          color: map-get($value, 'color');

          &:hover:not(:disabled) {
            background: transparent;
            border: 1px solid map-get($value, 'color');
          }

          &:active:not(:disabled) {
            border-color: darken(map-get($value, 'color'), 15%);
          }
        }

        &.isText {
          &:hover:not(:disabled) {
            color: map-get($value, 'color');
          }

          &:active:not(:disabled) {
            color: darken(map-get($value, 'color'), 10%);
          }
        }

        &.isTextUnderline {
          color: map-get($value, 'color');

          &:hover:not(:disabled) {
            color: map-get($value, 'color-hover');
          }

          &:active:not(:disabled) {
            color: darken(map-get($value, 'color'), 10%);
          }
        }

        &.isTextUnderline2 {

          &:hover:not(:disabled) {
            color: map-get($value, 'color');

            &::before {
              background: map-get($value, 'color');
            }
          }

          &:active:not(:disabled) {
            color: darken(map-get($value, 'color'), 10%);

            &::before {
              background: darken(map-get($value, 'color'), 10%);
            }
          }
        }
      }
    }

    &.isField {
      z-index: -1;
      max-width: none;
      height: 54px;
      padding: 0 1rem;
      margin: -1px -22px;
      font-size: 1rem;
      color: $color-gray-6;
      background: #FFF;
      border: 1px solid #DCDFE6;
      border-top-left-radius: 6px;
      border-bottom-left-radius: 6px;

      @include max-md {
        height: 40px;
        font-size: 0.875rem;
      }

      &:disabled, &.isDisabled {
        color: #C0C4CC !important;
        background-color: #F5F7FA !important;
      }

      &:hover:not(:disabled) {
        color: $color-primary;
        background: #FFF;
        border-color: #B1BACA;
      }

      &:active:not(:disabled) {
        color: darken($color-primary, 10%);
      }
    }

    &.isText {
      padding: 0;
      font-size: inherit;
      color: inherit;
      letter-spacing: inherit;
      background: transparent;
      border-color: transparent;

      &:disabled, &.isDisabled {
        background: transparent !important;
      }

      &:hover:not(:disabled) {
        background: transparent;
        border-color: transparent !important;
      }

      &:active:not(:disabled) {
        background: transparent;
        border-color: transparent !important;
      }
    }

    &.isTextUnderline {
      padding: 0;
      font-size: inherit;
      color: inherit;
      text-decoration: underline;
      letter-spacing: inherit;
      background: transparent;
      border-color: transparent;

      &:disabled, &.isDisabled {
        background: transparent !important;
      }

      &:hover:not(:disabled) {
        background: transparent;
        border-color: transparent !important;
      }

      &:active:not(:disabled) {
        background: transparent;
        border-color: transparent !important;
      }
    }

    &.isTextUnderline2 {
      position: relative;
      padding: 0;
      font-size: inherit;
      color: $color-gray-2;
      text-decoration: none;
      letter-spacing: inherit;
      background: transparent;
      border-color: transparent;

      &:disabled, &.isDisabled {
        background: transparent !important;
      }

      &:hover:not(:disabled) {
        background: transparent;
        border-color: transparent !important;
      }

      &:active:not(:disabled) {
        background: transparent;
        border-color: transparent !important;
      }

      &::before {
        position: absolute;
        bottom: 0;
        width: 100%;
        height: 1px;
        content: '';
        background: $color-gray-2;
      }
    }

    &.isBlock {
      width: 100%;
      padding-top: 100%;
      color: $color-gray-2;
      background: transparent;
      border: 2px solid $color-gray-4;
      border-radius: 24px;

      .baseButton {
        &__content {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        }

        &__icon {
          margin-bottom: 1rem;
          font-size: 2rem;
        }

        &__title {
          font-size: 1.25rem;
          font-weight: 600;

          @include max-md {
            font-size: 1rem;
          }
        }
      }

      &:hover {
        color: #FFF;
        border-color: $color-primary;
      }
    }

    &.isRadius {
      border-radius: 5px;

      // &.isSmall {
      //   border-radius: 6px;
      // }
    }

    &.isFillet {
      border-radius: 9527px;
    }

    &.isAuto {
      width: auto;
    }

    &.isFull {
      width: 100%;
    }

    &.isSmall {
      padding: 0.563rem 1.126rem;
    }

    @for $i from 1 through 12 {
      &.isCol#{$i} {
        width: #{(($container - ($container-gap * 2)) - (((12 / $i) - 1)) * ($container-gap * 2)) / (12 / $i)};
      }
      &.isCol1 {
        padding-right: 0.5rem;
        padding-left: 0.5rem;
      }
    }

    &.isOutline {
      background: transparent;

      &:disabled, &.isDisabled {
        background: transparent !important;
        border-color: $color-gray-4 !important;
      }

      &:hover:not(:disabled) {
        color: #FFF;
      }
    }

    &.isOutline2 {
      color: $color-gray-6;
      background: transparent;
      border: 1px solid $color-gray-6;

      &:disabled, &.isDisabled {
        background: transparent !important;
        border-color: $color-gray-4 !important;
      }

      &:hover:not(:disabled) {
        color: #FFF;
      }
    }

    &.isOutline3 {
      background: transparent;
      border: 1px solid $color-gray-3;

      &:disabled, &.isDisabled {
        background: transparent !important;
        border-color: $color-gray-4 !important;
      }
    }

    &.isCircle {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 60px;
      height: 60px;
      padding: 0 0 0 4px;
      border-radius: 100%;

      &.isSmall {
        width: 44px;
        height: 44px;
      }

      &.isSmaller {
        width: 36px;
        height: 36px;
      }
    }

    &.isLoading {
      position: relative;
      pointer-events: none;
      cursor: wait;

      .baseButton__content {
        opacity: 0;
      }

      &::after {
        position: absolute;
        top: calc(50% - .6em);
        left: calc(50% - .6em);
        display: block;
        width: 1.2em;
        height: 1.2em;
        content: "";
        border: 2.5px solid;
        border-top-color: transparent;
        border-right-color: transparent;
        border-radius: 290486px;
        animation: spinAround 0.6s infinite linear;
      }
    }

    // &.isLogin {
    //   position: relative;

    //   &::before {
    //     position: absolute;
    //     top: -0.25rem;
    //     right: -0.25rem;
    //     width: 6px;
    //     height: 6px;
    //     content: "";
    //     background-color: $color-primary;
    //     border-radius: 50%;
    //   }
    // }
  }

  @keyframes spinAround {
    0% {
      transform: rotate(0turn);
    }
    100% {
      transform: rotate(1turn);
    }
  }
</style>